import { useState } from "react";
import { Box, Collapse, IconButton, Stack, Typography, CircularProgress } from "@mui/material";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * Renders one Flowenti MCP tool invocation inside an AI Studio message:
 * name + arguments while it's running (no matching tool_result part yet),
 * then a collapsible result once it lands. There is no equivalent in
 * components/aiChat/ today -- ai_guide/ai_model/ai_robot never surface
 * which backend action ran, they only stream prose.
 */

type ToolCallCardProps = {
  name: string;
  arguments: Record<string, unknown>;
  /** undefined while the tool is still executing (no tool_result yet) */
  result?: { isError: boolean; content: string };
};

const ToolCallCard: React.FC<ToolCallCardProps> = ({ name, arguments: args, result }) => {
  const [expanded, setExpanded] = useState(false);
  const isRunning = result === undefined;
  const argsPreview = Object.keys(args).length > 0 ? JSON.stringify(args) : "";

  return (
    <Box
      sx={{
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: (theme) =>
          theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
        my: 0.5,
        minWidth: 240,
        maxWidth: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 1.5,
          py: 1,
          cursor: result ? "pointer" : "default",
        }}
        onClick={() => result && setExpanded((e) => !e)}
      >
        {isRunning ? (
          <CircularProgress size={16} thickness={5} />
        ) : result.isError ? (
          <ErrorOutlineIcon fontSize="small" color="error" />
        ) : (
          <CheckCircleOutlineIcon fontSize="small" color="success" />
        )}
        <BuildOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
        <Typography
          variant="body2"
          sx={{ fontFamily: "monospace", fontWeight: 600, minWidth: 0 }}
          noWrap
        >
          {name}
        </Typography>
        {argsPreview && (
          <Typography
            variant="caption"
            sx={{ fontFamily: "monospace", color: "text.secondary", minWidth: 0 }}
            noWrap
          >
            {argsPreview}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {result && (
          <IconButton
            size="small"
            aria-label={expanded ? "collapse" : "expand"}
            sx={{
              transform: expanded ? "rotate(180deg)" : "none",
              transition: "transform 0.15s",
            }}
          >
            <ExpandMoreIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
      {result && (
        <Collapse in={expanded}>
          <Box
            sx={{
              px: 1.5,
              pb: 1.5,
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              pt: 1,
            }}
          >
            <Typography
              variant="caption"
              component="pre"
              sx={{
                fontFamily: "monospace",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: result.isError ? "error.main" : "text.secondary",
                m: 0,
              }}
            >
              {result.content}
            </Typography>
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

export default ToolCallCard;
